import React, { useState, useEffect } from 'react';
import { 
  Layout,
  BookOpen,
  FileText,
  Settings,
  Shield,
  Users,
  Eye,
  Code,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Edit,
  Save,
  Plus,
  Search,
  Monitor,
  Play,
  Database,
  Lock,
  AlertCircle,
  HelpCircle,
  Image,
  Activity,
  RefreshCw,
  Loader,
  Server
} from 'lucide-react';

// Import dynamique de tous les fichiers JSON du répertoire specifications
const specContext = require.context('../data/specifications', false, /\.json$/);

// Mapping des icônes par type
const SPEC_ICONS = {
  'environments': Server,
  'agents': Activity,
  'functions': Code,
  'tasks': Play,
  'anomalies': AlertTriangle,
  'supervision': Monitor,
  'security': Shield,
  'users': Users,
  'database': Database,
  'api': Code,
  'interface': Layout,
  'reports': FileText,
  'default': BookOpen
};

// Helper pour détecter le type de spécification
const detectSpecificationType = (metadata, filename) => {
  const name = metadata?.title?.toLowerCase() || '';
  const filenameLC = filename.toLowerCase();
  
  // Détection par nom de fichier
  for (const [key, icon] of Object.entries(SPEC_ICONS)) {
    if (filenameLC.includes(key) || name.includes(key)) {
      return {
        icon,
        name: metadata?.title || key.charAt(0).toUpperCase() + key.slice(1),
        color: getColorForType(key)
      };
    }
  }
  
  return {
    icon: SPEC_ICONS.default,
    name: metadata?.title || 'Spécification',
    color: '#6B7280'
  };
};

// Helper pour obtenir la couleur par type
const getColorForType = (type) => {
  const colors = {
    'environments': '#3B82F6',
    'agents': '#10B981',
    'functions': '#6366F1',
    'tasks': '#F59E0B',
    'anomalies': '#EF4444',
    'supervision': '#8B5CF6',
    'security': '#EC4899',
    'users': '#06B6D4',
    'database': '#6366F1',
    'api': '#10B981',
    'interface': '#3B82F6',
    'reports': '#F59E0B'
  };
  return colors[type] || '#6B7280';
};

// Validation de structure d'une spécification
const validateSpecificationStructure = (data, filename) => {
  const errors = [];
  
  // Vérifier les métadonnées
  if (!data.metadata) {
    errors.push('Métadonnées manquantes');
  }
  
  // Vérifier les pages
  if (!data.pages || !Array.isArray(data.pages)) {
    errors.push('Pages manquantes ou invalides');
  }
  
  // Vérifier que chaque page a les champs requis
  if (data.pages) {
    data.pages.forEach((page, index) => {
      if (!page.id) errors.push(`Page ${index}: id manquant`);
      if (!page.title) errors.push(`Page ${index}: titre manquant`);
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings: []
  };
};

// Extraire les métadonnées d'une spécification
const extractSpecificationMetadata = (data, filename) => {
  const typeInfo = detectSpecificationType(data.metadata, filename);
  
  return {
    filename,
    name: typeInfo.name,
    icon: typeInfo.icon,
    color: typeInfo.color,
    description: data.metadata?.description || '',
    version: data.metadata?.version || '1.0.0',
    pagesCount: data.pages?.length || 0
  };
};

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Composant de visualisation des pré-conditions
const PreConditionsSection = ({ preConditions }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!preConditions) return null;

  return (
    <Card className="mb-6">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Shield className="h-5 w-5 text-blue-500" />
            <div>
              <CardTitle>Pré-conditions</CardTitle>
              <p className="text-sm text-gray-600">Conditions requises pour l'utilisation</p>
            </div>
          </div>
          <ChevronDown 
            className={`h-5 w-5 transform transition-transform ${
              isExpanded ? '' : '-rotate-90'
            }`} 
          />
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            {/* Exigences système */}
            {preConditions.systemRequirements && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Exigences Système
                </h4>
                <div className="space-y-2">
                  {preConditions.systemRequirements.map((req, index) => (
                    <div 
                      key={index} 
                      className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <Settings className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-700">{req.description}</p>
                        {req.critical && (
                          <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium bg-red-100 text-red-800">
                            Critique
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Exigences utilisateur */}
            {preConditions.userRequirements && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Exigences Utilisateur
                </h4>
                <div className="space-y-2">
                  {preConditions.userRequirements.map((req, index) => (
                    <div 
                      key={index} 
                      className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <Users className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-700">{req.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Exigences données */}
            {preConditions.dataRequirements && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Exigences Données
                </h4>
                <div className="space-y-2">
                  {preConditions.dataRequirements.map((req, index) => (
                    <div 
                      key={index} 
                      className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <Database className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-700">{req.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant de visualisation de l'interface
const InterfaceSection = ({ interface: uiInterface }) => {
  const [activeTab, setActiveTab] = useState('layout');

  if (!uiInterface) return null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Layout className="h-5 w-5 text-blue-500" />
          <span>Interface Utilisateur</span>
        </CardTitle>
      </CardHeader>
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          {[
            { id: 'layout', label: 'Structure', icon: Layout },
            { id: 'components', label: 'Composants', icon: Monitor },
            { id: 'states', label: 'États', icon: Activity },
            { id: 'dialogs', label: 'Dialogues', icon: HelpCircle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } flex items-center px-4 py-2 border-b-2 text-sm font-medium`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      <CardContent>
        {activeTab === 'layout' && (
          <div className="space-y-4">
            {uiInterface.layout && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Type de mise en page
                </h4>
                <p className="text-sm text-gray-600">{uiInterface.layout.type}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(uiInterface.layout)
                    .filter(([key]) => key !== 'type')
                    .map(([key, value]) => (
                      <span
                        key={key}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {key}: {value.toString()}
                      </span>
                    ))}
                </div>
              </div>
            )}

            {/* Sections de l'interface */}
            {uiInterface.sections && (
              <div className="space-y-4">
                {uiInterface.sections.map((section) => (
                  <div 
                    key={section.id}
                    className="border rounded-lg p-4"
                  >
                    <h4 className="font-medium text-gray-900 mb-2">
                      {section.title || section.id}
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Type: {section.type}
                    </p>
                    
                    {/* Actions de section */}
                    {section.actions && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Actions :</h5>
                        <div className="flex flex-wrap gap-2">
                          {section.actions.map((action) => (
                            <span
                              key={action.id}
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                action.type === 'primary' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {action.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Onglets */}
                    {section.tabs && (
                      <div className="mt-3">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Onglets :</h5>
                        <div className="space-y-2">
                          {section.tabs.map((tab) => (
                            <div key={tab.id} className="flex items-center p-2 bg-gray-50 rounded">
                              <span className="text-sm font-medium text-gray-700">{tab.label}</span>
                              <span className="ml-2 text-xs text-gray-500">({tab.id})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'dialogs' && uiInterface.dialogs && (
          <div className="space-y-4">
            {uiInterface.dialogs.map((dialog) => (
              <div key={dialog.id} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{dialog.title}</h4>
                <p className="text-sm text-gray-600 mb-3">Type: {dialog.type}</p>
                
                {dialog.fields && (
                  <div className="mb-3">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Champs :</h5>
                    <div className="space-y-2">
                      {dialog.fields.map((field) => (
                        <div key={field.name} className="flex items-start p-2 bg-gray-50 rounded">
                          <div>
                            <p className="text-sm font-medium text-gray-700">{field.label}</p>
                            <p className="text-xs text-gray-500">
                              {field.name} • {field.type}
                              {field.required && ' • Requis'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {dialog.actions && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Actions :</h5>
                    <div className="flex flex-wrap gap-2">
                      {dialog.actions.map((action) => (
                        <span
                          key={action.id}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            action.type === 'primary' 
                              ? 'bg-blue-100 text-blue-800'
                              : action.type === 'danger'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {action.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'states' && uiInterface.states && (
          <div className="space-y-4">
            {uiInterface.states.map((state) => (
              <div
                key={state.id}
                className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {state.id}
                  </h4>
                  <p className="mt-1 text-sm text-gray-600">
                    {state.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Composant de visualisation des processus
const ProcessesSection = ({ processes }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!processes || processes.length === 0) return null;

  return (
    <Card className="mb-6">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Play className="h-5 w-5 text-blue-500" />
            <div>
              <CardTitle>Processus</CardTitle>
              <p className="text-sm text-gray-600">Processus métier détaillés</p>
            </div>
          </div>
          <ChevronDown 
            className={`h-5 w-5 transform transition-transform ${
              isExpanded ? '' : '-rotate-90'
            }`} 
          />
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            {processes.map((process) => (
              <div key={process.id} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{process.name}</h4>
                <p className="text-sm text-gray-600 mb-4">{process.description}</p>
                
                {process.steps && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-gray-700">Étapes :</h5>
                    <div className="space-y-2">
                      {process.steps.map((step, index) => (
                        <div key={index} className="flex items-start space-x-3 p-2 bg-gray-50 rounded">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">
                            {step.order || index + 1}
                          </span>
                          <div>
                            <p className="text-sm text-gray-700">{step.description}</p>
                            {step.validation && (
                              <p className="text-xs text-gray-500 mt-1">
                                Validation: {step.validation}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant de visualisation des endpoints API
const ApiEndpointsSection = ({ apiEndpoints }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!apiEndpoints || apiEndpoints.length === 0) return null;

  return (
    <Card className="mb-6">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Code className="h-5 w-5 text-blue-500" />
            <div>
              <CardTitle>Endpoints API</CardTitle>
              <p className="text-sm text-gray-600">Points d'accès de l'API</p>
            </div>
          </div>
          <ChevronDown 
            className={`h-5 w-5 transform transition-transform ${
              isExpanded ? '' : '-rotate-90'
            }`} 
          />
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-4">
            {apiEndpoints.map((endpoint, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                    endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                    endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                    endpoint.method === 'DELETE' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {endpoint.method}
                  </span>
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {endpoint.path}
                  </code>
                </div>
                <p className="text-sm text-gray-600 mb-3">{endpoint.description}</p>
                
                {endpoint.requestFields && (
                  <div className="mb-2">
                    <h5 className="text-xs font-medium text-gray-700 mb-1">Champs requis :</h5>
                    <div className="flex flex-wrap gap-1">
                      {endpoint.requestFields.map((field) => (
                        <span key={field} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {endpoint.responseFields && (
                  <div>
                    <h5 className="text-xs font-medium text-gray-700 mb-1">Champs de réponse :</h5>
                    <div className="flex flex-wrap gap-1">
                      {endpoint.responseFields.map((field) => (
                        <span key={field} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant de visualisation des post-conditions
const PostConditionsSection = ({ postConditions }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!postConditions) return null;

  return (
    <Card className="mb-6">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div>
              <CardTitle>Post-conditions</CardTitle>
              <p className="text-sm text-gray-600">États résultants après exécution</p>
            </div>
          </div>
          <ChevronDown 
            className={`h-5 w-5 transform transition-transform ${
              isExpanded ? '' : '-rotate-90'
            }`} 
          />
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            {/* État système */}
            {postConditions.systemState && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  État Système
                </h4>
                <div className="space-y-2">
                  {postConditions.systemState.map((state, index) => (
                    <div 
                      key={index} 
                      className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg"
                    >
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-700">{state.description}</p>
                        {state.table && (
                          <p className="text-xs text-gray-500 mt-1">
                            Table: {state.table}
                            {state.status && ` - Status: ${state.status}`}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* État utilisateur */}
            {postConditions.userState && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  État Utilisateur
                </h4>
                <div className="space-y-2">
                  {postConditions.userState.map((state, index) => (
                    <div 
                      key={index} 
                      className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg"
                    >
                      <Users className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-700">{state.description}</p>
                        {state.url && (
                          <p className="text-xs text-gray-500 mt-1">
                            URL: {state.url}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* État données */}
            {postConditions.dataState && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  État Données
                </h4>
                <div className="space-y-2">
                  {postConditions.dataState.map((state, index) => (
                    <div 
                      key={index} 
                      className="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg"
                    >
                      <Database className="h-5 w-5 text-purple-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-700">{state.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant principal de page
const PageDocumentation = ({ page }) => {
  if (!page) return null;

  return (
    <div className="space-y-6">
      {/* En-tête de la page */}
      <div className="bg-white px-6 py-4 shadow rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{page.title}</h2>
            <p className="mt-1 text-gray-600">{page.description}</p>
            <div className="mt-2 flex items-center space-x-2 text-sm text-gray-500">
              <span>Catégorie: {page.category}</span>
              <span>•</span>
              <span>ID: {page.id}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              v{page.version}
            </span>
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
              {page.status}
            </span>
          </div>
        </div>
      </div>

      {/* Sections principales */}
      <PreConditionsSection preConditions={page.preConditions} />
      <InterfaceSection interface={page.interface} />
      <ProcessesSection processes={page.processes} />
      <ApiEndpointsSection apiEndpoints={page.apiEndpoints} />
      <PostConditionsSection postConditions={page.postConditions} />
    </div>
  );
};

// Composant principal de documentation
const DocumentationFonctionnelle = () => {
  const [specifications, setSpecifications] = useState([]);
  const [selectedSpecification, setSelectedSpecification] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger toutes les spécifications au montage
  useEffect(() => {
    loadSpecifications();
  }, []);

  const loadSpecifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Démarrage du chargement des spécifications...');
      
      const keys = specContext.keys();
      const loadedSpecifications = [];
      const failedFiles = [];
      
      // Parcourir tous les fichiers trouvés
      keys.forEach(key => {
        const filename = key.replace('./', '');
        
        try {
          // Charger le fichier
          const data = specContext(key);
          
          // Valider la structure
          const validation = validateSpecificationStructure(data, filename);
          
          if (validation.isValid) {
            // Extraire les métadonnées
            const metadata = extractSpecificationMetadata(data, filename);
            
            loadedSpecifications.push({
              ...metadata,
              data
            });
          } else {
            failedFiles.push({
              filename,
              errors: validation.errors
            });
          }
        } catch (err) {
          failedFiles.push({
            filename,
            error: err.message
          });
          console.error(`Erreur lors du chargement de ${filename}:`, err);
        }
      });
      
      console.log(`📁 ${loadedSpecifications.length} spécifications chargées`);
      
      // Trier par nom
      loadedSpecifications.sort((a, b) => a.name.localeCompare(b.name));
      
      setSpecifications(loadedSpecifications);
      if (loadedSpecifications.length > 0) {
        setSelectedSpecification(loadedSpecifications[0]);
        console.log(`✅ Première spécification sélectionnée: ${loadedSpecifications[0].name}`);
      }
      
      // Log des fichiers avec erreurs (pour debug)
      if (failedFiles.length > 0) {
        console.log('⚠️ Fichiers avec erreurs:', failedFiles);
      }
    } catch (err) {
      setError('Erreur lors du chargement des spécifications');
      console.error('Error loading specifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les pages en fonction de la recherche
  const filteredPages = selectedSpecification?.data?.pages?.filter(page =>
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.description.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-2 text-gray-600">Chargement des spécifications...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent>
              <div className="flex items-center space-x-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
              <Button
                onClick={loadSpecifications}
                className="mt-4"
                variant="outline"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Documentation Fonctionnelle
            </h1>
            <p className="mt-1 text-gray-600">
              {specifications.length} spécifications chargées
            </p>
          </div>
          <Button
            variant="outline"
            onClick={loadSpecifications}
            className="flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Actualiser</span>
          </Button>
        </div>
      </div>

      {/* Sélection de spécification */}
      <div className="mb-6">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {specifications.map((specification) => {
            const Icon = specification.icon;
            return (
              <button
                key={specification.filename}
                onClick={() => {
                  setSelectedSpecification(specification);
                  setSelectedPage(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex items-center space-x-2 ${
                  selectedSpecification?.filename === specification.filename
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{specification.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {selectedSpecification && (
        <>
          {/* Informations de la spécification */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-500" />
                <span>{selectedSpecification.data.metadata.title}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                {selectedSpecification.data.metadata.description}
              </p>
              <div className="mt-4 text-sm text-gray-500">
                Version {selectedSpecification.data.metadata.version} • 
                {filteredPages.length} pages
              </div>
            </CardContent>
          </Card>

          {/* Barre de recherche */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher une page..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>

          {/* Navigation et contenu */}
          <div className="grid grid-cols-12 gap-6">
            {/* Sidebar */}
            <div className="col-span-3">
              <div className="sticky top-8 space-y-6">
                {/* Liste des pages */}
                <nav className="space-y-1">
                  {filteredPages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => setSelectedPage(page)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                        selectedPage?.id === page.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <FileText className="h-5 w-5 mr-3" />
                      <div className="text-left flex-1">
                        <div>{page.title}</div>
                        <div className="text-xs text-gray-500">{page.category}</div>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="col-span-9">
              {selectedPage ? (
                <PageDocumentation page={selectedPage} />
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    Sélectionnez une page
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choisissez une page dans le menu pour voir sa documentation détaillée
                  </p>
                  {filteredPages.length === 0 && selectedSpecification && (
                    <p className="mt-2 text-sm text-gray-400">
                      Aucune page trouvée pour "{searchTerm}"
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentationFonctionnelle;